import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Alert,
  Collapse
} from '@mui/material'
import { Bank } from '@otw/models'
import { CreateUserType, PrivateUserInfoType } from '@otw/models/user'
import { useAdminApplications } from 'hooks/admin/useAdminApplications'
import { useManageUsers } from 'hooks/admin/useManageUsers'
import { apiClient } from '@/core/api/apiClient'
import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

export interface CreateDisabledUserInitialValues {
  first_name?: string
  last_name?: string
  email?: string
  phone?: string
  company_name?: string
}

interface CreateDisabledUserProps {
  onSuccess?: (userData: PrivateUserInfoType) => void
  silent?: boolean
  requirePhone?: boolean
  /** When set, dialog open state is controlled by the parent (no default trigger button). */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Hide the default "Create New Client" trigger (use with controlled `open`). */
  hideTrigger?: boolean
  /** Prefill form fields when the dialog opens. */
  initialValues?: CreateDisabledUserInitialValues
}

// Base schema builder so we can optionally require phone
const buildCreateUserSchema = (requirePhone: boolean) =>
  z.object({
    first_name: z.string().min(1, 'First name is required'),
    last_name: z.string().min(1, 'Last name is required'),
    email: z.string().email('Invalid email address'),
    phone: requirePhone
      ? z
          .string({ required_error: 'Phone number is required' })
          .min(1, 'Phone number is required')
          .refine(
            val => /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(val || ''),
            'Please enter a valid phone number',
          )
      : z
          .string()
          .optional()
          .refine(
            val => !val || /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/.test(val),
            'Please enter a valid phone number',
          ),
    company_name: z.string().optional(),
    bank_id: z.string().optional(),
    external_bank_affiliation_name: z.string().optional(),
    notes: z.string().optional(),
  })

type FormData = z.infer<ReturnType<typeof buildCreateUserSchema>>

/** Admin create-user endpoints return `userId`; some legacy paths use `id`. */
const getCreatedUserId = (responseData: unknown): string => {
  if (!responseData || typeof responseData !== 'object') return ''
  const data = responseData as { userId?: string; id?: string }
  return (data.userId ?? data.id ?? '').trim()
}

export const CreateDisabledUser: React.FC<CreateDisabledUserProps> = ({
  onSuccess,
  silent = false,
  requirePhone = false,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
  initialValues,
}) => {
  const { createDisabledUserMutation } = useManageUsers()
  const { banks } = useAdminApplications()
  const [isCustomBank, setIsCustomBank] = useState(false)
  const [customBankName, setCustomBankName] = useState('')
  const [internalOpen, setInternalOpen] = useState(false)
  const [sendInvite, setSendInvite] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
    setValue,
  } = useForm<FormData>({
    resolver: zodResolver(buildCreateUserSchema(requirePhone)),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_name: '',
      bank_id: '',
      external_bank_affiliation_name: '',
      notes: '',
    },
  })

  const resetDialogState = () => {
    reset({
      first_name: initialValues?.first_name ?? '',
      last_name: initialValues?.last_name ?? '',
      email: initialValues?.email ?? '',
      phone: initialValues?.phone ?? '',
      company_name: initialValues?.company_name ?? '',
      bank_id: '',
      external_bank_affiliation_name: '',
      notes: '',
    })
    setIsCustomBank(false)
    setCustomBankName('')
    setSendInvite(false)
    setErrorMessage(null)
  }

  const setOpenState = (next: boolean) => {
    if (isControlled) onOpenChange?.(next)
    else setInternalOpen(next)
  }

  const handleOpen = () => {
    resetDialogState()
    setOpenState(true)
  }

  const handleClose = () => {
    setOpenState(false)
    resetDialogState()
  }

  useEffect(() => {
    if (!open) return
    resetDialogState()
  }, [open, initialValues?.first_name, initialValues?.last_name, initialValues?.email, initialValues?.phone, initialValues?.company_name])

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true)
    setErrorMessage(null)

    try {
      // Check if user already exists
      const userExists = await apiClient.portal.admin.checkUserExists(data.email)

      if (userExists) {
        setErrorMessage(`A user with email ${data.email} already exists.`)
        setIsSubmitting(false)
        return
      }

      const userData: CreateUserType = {
        ...data,
        password: '', // Generated server-side
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        email: data.email || '',
        phone: data.phone || '',

        company_name: data.company_name || '',
        bank_id: isCustomBank ? '1' : data.bank_id === 'cash' ? '' : data.bank_id, // Handle cash specially
        external_financing_source: isCustomBank ? customBankName : data.bank_id === 'cash' ? 'Cash' : '',
        dealership_name: '',
        role: 'client',
        permissions: [],
        email_verified: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        needsOnboarding: true,
        disabled: !sendInvite, // Set disabled based on sendInvite
        is_active: sendInvite, // Set is_active based on sendInvite
      }

      if (sendInvite) {
        // axiosClient already returns response.data (not a fetch Response)
        const responseData = await apiClient.portal.admin.createFirebaseUser(userData)
        const createdUserId = getCreatedUserId(responseData)
        if (!createdUserId) {
          throw new Error('Failed to create user with invite')
        }

        const completeUserData: PrivateUserInfoType = {
          ...userData,
          id: createdUserId,
          displayName: `${data.first_name} ${data.last_name}`,
          email_verified: false,
          createdAt: userData.createdAt || new Date().toISOString(),
          updatedAt: userData.updatedAt || new Date().toISOString(),
          role: 'client',
          permissions: [],
          disabled: false,
          is_active: true,
        }

        onSuccess?.(completeUserData)
        handleClose()
        setIsSubmitting(false)
      } else {
        // Use createDisabledUserMutation if not sending invite
        createDisabledUserMutation.mutate(userData, {
          onSuccess: async (response) => {
            try {
              // Parse the response JSON if it's a Response object
              const responseData = response instanceof Response ? await response.json() : response

              // Create a properly formatted PrivateUserInfoType object
              const completeUserData: PrivateUserInfoType = {
                ...userData,
                id: getCreatedUserId(responseData),
                displayName: `${data.first_name} ${data.last_name}`,
                email_verified: userData.email_verified || false,
                createdAt: userData.createdAt || new Date().toISOString(),
                updatedAt: userData.updatedAt || new Date().toISOString(),
                role: userData.role || 'client',
                permissions: userData.permissions || [],
                disabled: true,
                is_active: false,
              }

              onSuccess?.(completeUserData)
              handleClose()
            } catch (error) {
              console.error('Error processing user data:', error)
            } finally {
              setIsSubmitting(false)
            }
          },
          onError: error => {
            console.error('Error creating user', error)
            setErrorMessage(
              error instanceof Error
                ? error.message
                : 'Failed to create client. Please try again.',
            )
            setIsSubmitting(false)
          },
        })
      }
    } catch (error) {
      console.error('Error in user creation:', error)
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred')
      setIsSubmitting(false)
    }
  }

  return (
    <>
      {!hideTrigger && (
        <Button variant='outlined' onClick={handleOpen} size="small" aria-label="Create User">
          Create New Client
        </Button>
      )}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Create New Client</DialogTitle>
        <DialogContent>
          <Collapse in={!!errorMessage}>
            <Alert 
              severity="error" 
              sx={{ mt: 2, mb: 2 }}
              onClose={() => setErrorMessage(null)}
            >
              {errorMessage}
            </Alert>
          </Collapse>
          
          <Box component='form' noValidate onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Contact Information
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label='First Name'
                  {...register('first_name')}
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message}
                  disabled={isSubmitting}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label='Last Name'
                  {...register('last_name')}
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  label='Email Address'
                  {...register('email')}
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label='Phone Number'
                  {...register('phone')}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  disabled={isSubmitting}
                  placeholder="(123) 456-7890"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }} gutterBottom>
                  Company Details
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label='Company Name'
                  {...register('company_name')}
                  error={!!errors.company_name}
                  helperText={errors.company_name?.message}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.bank_id}>
                  <InputLabel>Bank Affiliation</InputLabel>
                  <Controller
                    name="bank_id"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Bank Affiliation"
                        onChange={(e) => {
                          if (e.target.value === 'other') {
                            setIsCustomBank(true)
                            setValue('bank_id', 'other')
                          } else if (e.target.value === 'cash') {
                            setIsCustomBank(false)
                            setValue('bank_id', 'cash') // Use 'cash' as a special bank_id value
                            setValue('external_bank_affiliation_name', 'Cash') // Set external name to Cash
                            field.onChange(e) // Still trigger the field change
                          } else {
                            setIsCustomBank(false)
                            field.onChange(e)
                          }
                        }}
                        disabled={isSubmitting}
                      >
                        <MenuItem value="">
                          <em>Select a bank</em>
                        </MenuItem>
                        {banks.data?.map((bank: Bank) => (
                          <MenuItem key={bank.id} value={bank.id}>
                            {bank.name}
                          </MenuItem>
                        ))}
                        <MenuItem value="cash" sx={{ color: 'primary.main' }}>
                          Cash
                        </MenuItem>
                        <MenuItem value="other" sx={{ color: 'primary.main' }}>
                          Other
                        </MenuItem>
                      </Select>
                    )}
                  />
                  <FormHelperText>
                    {isCustomBank 
                      ? "Please enter the bank name below"
                      : errors.bank_id?.message}
                  </FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} sx={{ mt: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={sendInvite}
                      onChange={(e) => setSendInvite(e.target.checked)}
                      color="primary"
                      disabled={isSubmitting}
                    />
                  }
                  label="Send invite email to client"
                />
              </Grid>

              {isCustomBank && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label='Bank Name'
                    value={customBankName}
                    onChange={(e) => setCustomBankName(e.target.value)}
                    disabled={isSubmitting}
                    error={!!errors.bank_id}
                    helperText={errors.bank_id?.message}
                    autoFocus
                    placeholder="Enter bank name"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }} gutterBottom>
                  Additional Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label='Notes'
                  {...register('notes')}
                  error={!!errors.notes}
                  helperText={errors.notes?.message}
                  disabled={isSubmitting}
                  placeholder="Enter any additional notes about this client..."
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type='submit'
                  fullWidth
                  variant='contained'
                  sx={{ mt: 3, mb: 2 }}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Create Client'
                  )}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  )
} 